import { useState } from 'react';
import { useCallPlans, useSaveCallPlan } from '../../lib/api/hooks-products.js';
import { useRateDecks } from '../../lib/api/hooks-billing.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatMoney } from '../../lib/format.js';
import { useSession } from '../../lib/auth/session.js';

/**
 * Call plans: the product a customer is sold. The rate deck is how it is
 * implemented, so several plans can share one deck with different monthly fees
 * or included minutes.
 */
export function CallPlansPage() {
  const can = useSession((s) => s.can);
  const plans = useCallPlans();
  const [adding, setAdding] = useState(false);
  /**
   * The plan being edited, which is how a rate sheet reaches a plan that was created without one
   * (owner, 25 September 2026: "theres no ratesheet attached to the calling plan and I dont see
   * where I can attach it"). The form was create-only, so a plan made in a hurry could never be
   * finished.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const plan = plans.data?.data.find((p) => p.id === editing) ?? null;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Call plans</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            What customers are sold. Each points at a sell rate sheet that prices the calls.
          </p>
        </div>
        {can('ratedeck:write') && (
          <button type="button" className="arc-btn arc-btn-primary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : '+ New plan'}
          </button>
        )}
      </div>

      {plans.error && <ErrorBanner error={plans.error} />}
      {adding && <PlanForm onDone={() => setAdding(false)} />}
      {plan && <PlanForm existing={plan} onDone={() => setEditing(null)} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Plan</th><th>Rate sheet</th><th>Billing</th>
                <th className="arc-td-right">Monthly</th>
                <th className="arc-td-right">Included</th>
                <th className="arc-td-right">Accounts</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            {plans.data === undefined ? <TableSkeleton cols={8} /> : (
              <tbody>
                {plans.data?.data.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="arc-stack">
                        <span style={{ fontWeight: 570 }}>{p.name}</span>
                        <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{p.key}</span>
                      </div>
                    </td>
                    <td>
                      {p.rate_deck_name
                        ? <span>{p.rate_deck_name}</span>
                        : can('ratedeck:write')
                          ? (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              onClick={() => setEditing(p.id)}>
                              <span className="arc-badge arc-badge-warn">no rate sheet</span>
                              <span style={{ marginLeft: 6 }}>attach one</span>
                            </button>
                          )
                          : <span className="arc-badge arc-badge-warn">no rate sheet</span>}
                    </td>
                    <td><Badge tone={p.billing_mode === 'prepaid' ? 'info' : 'muted'}>{p.billing_mode}</Badge></td>
                    <td className="arc-td-right arc-num" style={{ fontWeight: 600 }}>
                      {formatMoney(p.monthly_fee, p.currency)}
                    </td>
                    <td className="arc-td-right arc-num">
                      {p.included_minutes ? `${p.included_minutes} min` : '—'}
                    </td>
                    <td className="arc-td-right arc-num">{p.tenant_count ?? 0}</td>
                    <td><Badge tone={p.is_active ? 'ok' : 'muted'}>{p.is_active ? 'active' : 'inactive'}</Badge></td>
                    <td className="arc-td-right">
                      {can('ratedeck:write') && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => setEditing(editing === p.id ? null : p.id)}>
                          {editing === p.id ? 'Close' : 'Edit'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {plans.data !== undefined && plans.data?.data.length === 0 && (
          <Empty icon="plan" title="No call plans"
            hint="A plan wraps a rate sheet with a monthly fee — it is what appears on a quote." />
        )}
      </div>
    </>
  );
}

/**
 * One form for both, because a plan is edited by writing it again.
 *
 * The API upserts on the plan's KEY, so saving an existing plan with the same key is the edit --
 * no second route, and no way for the two paths to disagree about what a plan is. The key is
 * therefore fixed once a plan exists: changing it would quietly create a second plan and leave
 * the accounts on the first one.
 */
function PlanForm({ existing, onDone }: {
  existing?: {
    id: string; key: string; name: string; sell_rate_deck_id: string | null;
    monthly_fee: string; billing_mode: string; included_minutes: number | null;
    is_active: boolean; currency: string; tenant_count?: number | null | undefined;
  };
  onDone: () => void;
}) {
  const save = useSaveCallPlan();
  const decks = useRateDecks();
  const [name, setName] = useState(existing?.name ?? '');
  const [key, setKey] = useState(existing?.key ?? '');
  const [deckId, setDeckId] = useState(existing?.sell_rate_deck_id ?? '');
  const [fee, setFee] = useState(existing?.monthly_fee ?? '0');
  const [mode, setMode] = useState(existing?.billing_mode ?? 'prepaid');
  const [minutes, setMinutes] = useState(
    existing?.included_minutes == null ? '' : String(existing.included_minutes));
  const [active, setActive] = useState(existing?.is_active ?? true);
  const step = useStepUp();

  // Only SELL decks: a cost deck is our buy price, and selling from one would
  // price every call at zero margin. The API refuses it too.
  const sellDecks = (decks.data?.data ?? []).filter((d) => d.deck_type === 'sell');

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        /**
         * What a call costs is billing, so `ratedeck:write` is a step-up permission: the API
         * refuses until the person proves they are still themselves. Without asking, the refusal
         * went into a mutation nobody rendered and Create did nothing at all (owner, 25
         * September 2026: "i clicked create and nothing happened").
         */
        void step.run(() => save.mutateAsync({
          key, name, monthly_fee: fee, billing_mode: mode,
          included_minutes: minutes === '' ? 0 : Number(minutes),
          is_active: active,
          currency: existing?.currency ?? 'ZAR',
          /**
           * Null clears it. Sending nothing would leave the old sheet attached, so "Assign later"
           * on a plan that HAS one has to mean detach, not no-change -- the upsert writes every
           * field it is given.
           */
          sell_rate_deck_id: deckId || null,
        })).then((made) => { if (made) { if (!existing) { setName(''); setKey(''); } onDone(); } });
      }}
    >
      <h2 className="arc-h2" style={{ marginBottom: 14 }}>
        {existing ? `Edit ${existing.name}` : 'New call plan'}
      </h2>
      {existing && (existing.tenant_count ?? 0) > 0 && (
        <p className="arc-hint" style={{ marginTop: -8, marginBottom: 12 }}>
          {existing.tenant_count} account{existing.tenant_count === 1 ? '' : 's'} are on this plan.
          Changing the rate sheet changes what their next calls cost; calls already made keep the
          price they were rated at.
        </p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.3fr 130px 130px auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-name">Plan name</label>
          <input id="cp-name" className="arc-input" required value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!existing) {
                setKey(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
              }
            }}
            placeholder="29c (LB, LCR)" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-key">Key</label>
          <input id="cp-key" className="arc-input" required value={key} readOnly={!!existing}
            onChange={(e) => setKey(e.target.value)} />
          {existing && <div className="arc-hint">Fixed: it is what the plan is saved under.</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-deck">Sell rate sheet</label>
          <select id="cp-deck" className="arc-select" value={deckId}
            onChange={(e) => setDeckId(e.target.value)}>
            <option value="">Assign later</option>
            {sellDecks.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-fee">Monthly fee</label>
          <input id="cp-fee" className="arc-input" inputMode="decimal" value={fee}
            onChange={(e) => setFee(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-mode">Billing</label>
          <select id="cp-mode" className="arc-select" value={mode}
            onChange={(e) => setMode(e.target.value)}>
            <option value="prepaid">Prepaid</option>
            <option value="postpaid">Postpaid</option>
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cp-min">Included minutes</label>
          <input id="cp-min" className="arc-input arc-num" inputMode="numeric" value={minutes}
            placeholder="0" onChange={(e) => setMinutes(e.target.value.replace(/[^0-9]/g, ''))} />
        </div>
        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || step.busy}>
            {save.isPending || step.busy ? 'Saving…' : existing ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      {existing && (
        <div className="arc-row" style={{ gap: 9, marginTop: 12 }}>
          <input id="cp-active" type="checkbox" checked={active}
            onChange={(e) => setActive(e.target.checked)} />
          <label htmlFor="cp-active" style={{ fontSize: 12.5, cursor: 'pointer' }}>
            Offered to new accounts
            <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
              Switching it off leaves the accounts already on it exactly as they are.
            </span>
          </label>
        </div>
      )}

      {/* Whatever the API refused for, said here. A form that answers a click with silence is
          the one thing a form must never do. */}
      {save.error && <ErrorBanner error={save.error} />}
      <StepUpPrompt step={step} what={existing ? 'Changing a call plan' : 'Creating a call plan'}>
        A call plan sets what calls cost, so it is confirmed like anything else that moves money.
      </StepUpPrompt>
    </form>
  );
}
